import { Plus, X } from 'lucide-react';

interface StringListEditorProps {
  items: string[];
  onChange: (items: string[]) => void;
  max: number;
  maxLength: number;
  addLabel: string;
  placeholder?: string;
}

const inputCls =
  'flex-1 min-w-0 px-3 py-2 border border-gray-200 rounded-md bg-white text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-gold text-sm';

// A small reusable add/remove/edit editor for a list of short text strings.
// Order = display order; "Add" appends an empty row. Trimming/blank-filtering is left to
// the caller on save so the user can type freely.
export default function StringListEditor({ items, onChange, max, maxLength, addLabel, placeholder }: StringListEditorProps) {
  function updateAt(index: number, text: string) {
    onChange(items.map((it, i) => (i === index ? text : it)));
  }

  function removeAt(index: number) {
    onChange(items.filter((_, i) => i !== index));
  }

  function addItem() {
    if (items.length >= max) return;
    onChange([...items, '']);
  }

  return (
    <div className="space-y-2">
      {items.map((item, index) => (
        <div key={index} className="flex items-center gap-2">
          <input
            type="text"
            value={item}
            onChange={(e) => updateAt(index, e.target.value)}
            className={inputCls}
            maxLength={maxLength}
            placeholder={placeholder}
          />
          <button
            type="button"
            onClick={() => removeAt(index)}
            className="px-2 py-2 text-gray-400 hover:text-red-500 transition-colors flex-shrink-0"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      ))}

      <div className="flex items-center justify-between pt-1">
        <button
          type="button"
          onClick={addItem}
          disabled={items.length >= max}
          className="inline-flex items-center gap-1.5 text-sm text-gold hover:text-gold/80 disabled:text-gray-300 disabled:cursor-not-allowed transition-colors"
        >
          <Plus className="w-4 h-4" />
          {addLabel}
        </button>
        <span className="text-xs text-gray-400">{items.length}/{max}</span>
      </div>
    </div>
  );
}
